import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const productsJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/products`,
  url: `${siteUrl}/products`,
  name: '製品・工場案内｜特注ネジ加工事例・保有設備｜おかやまネジ製作所',
  description: '岡山市おかやまネジ製作所の製品・工場案内。チタン・ステンレス・インコネルなど難削材の特注ネジ加工事例、CNC旋盤・三次元測定機など保有設備を紹介。',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'トップ', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: '製品・工場案内', item: `${siteUrl}/products` },
    ],
  },
  mainEntity: {
    '@type': 'ItemList',
    name: '加工事例ギャラリー',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'チタン製特注ネジ', description: 'M3×10mm / 0.01mm精度' },
      { '@type': 'ListItem', position: 2, name: 'ステンレス精密ボルト', description: 'M5×15mm / 難削材対応' },
      { '@type': 'ListItem', position: 3, name: '特殊形状ネジ', description: '完全オーダーメイド' },
      { '@type': 'ListItem', position: 4, name: 'インコネル製ファスナー', description: '耐熱性 / 航空宇宙品質' },
      { '@type': 'ListItem', position: 5, name: '真鍮製精密ネジ', description: 'M4×12mm / 電気部品用' },
      { '@type': 'ListItem', position: 6, name: 'アルミニウム製ボルト', description: 'M6×20mm / 軽量化対応' },
      { '@type': 'ListItem', position: 7, name: '超小型精密ネジ', description: 'M1×3mm / 医療機器用' },
      { '@type': 'ListItem', position: 8, name: 'ハステロイ製ファスナー', description: 'M8×25mm / 化学プラント用' },
      { '@type': 'ListItem', position: 9, name: '特殊ピッチネジ', description: 'カスタムピッチ対応' },
    ],
  },
};

const ProductsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = '製品・工場案内｜特注ネジ加工事例・保有設備｜おかやまネジ製作所';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '岡山市おかやまネジ製作所の製品・工場案内。チタン・ステンレス・インコネルなど難削材の特注ネジ加工事例、CNC旋盤・三次元測定機など保有設備を紹介。');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${siteUrl}/products`);

    const scriptId = 'products-jsonld';
    if (document.getElementById(scriptId)) return;
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(productsJsonLd);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  const products = [
    {
      image: 'https://public.readdy.ai/ai/img_res/d9e3aae15861b500b51d1cf2302683cf.jpg',
      name: 'チタン製特注ネジ',
      specs: 'M3×10mm / 0.01mm精度',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=stainless%20steel%20precision%20bolt%20with%20hexagonal%20head%20on%20clean%20white%20background%2C%20macro%20close-up%20showing%20mirror-polished%20surface%20and%20perfect%20threading%2C%20industrial%20product%20photography&width=500&height=500&seq=prod-detail-002&orientation=squarish',
      name: 'ステンレス精密ボルト',
      specs: 'M5×15mm / 難削材対応',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=custom-shaped%20precision%20screw%20with%20unique%20thread%20design%20on%20clean%20white%20background%2C%20macro%20photography%20showing%20special%20geometric%20pattern%20and%20ultra-fine%20machining%20details&width=500&height=500&seq=prod-detail-003&orientation=squarish',
      name: '特殊形状ネジ',
      specs: '完全オーダーメイド',
    },
    {
      image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/dfed94c451ee5fe9b84358aadc7c9ff4.jpeg',
      name: 'インコネル製ファスナー',
      specs: '耐熱性 / 航空宇宙品質',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=brass%20precision%20screw%20with%20fine%20threading%20on%20clean%20white%20background%2C%20macro%20photography%20showing%20golden%20metal%20surface%20and%20detailed%20machining%20quality&width=500&height=500&seq=prod-detail-005&orientation=squarish',
      name: '真鍮製精密ネジ',
      specs: 'M4×12mm / 電気部品用',
    },
    {
      image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/df04aead7809793b9ebc00fd8df06acd.jpeg',
      name: 'アルミニウム製ボルト',
      specs: 'M6×20mm / 軽量化対応',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=micro%20precision%20screw%20extremely%20small%20size%20on%20clean%20white%20background%2C%20macro%20photography%20showing%20ultra-fine%20threading%20and%20miniature%20machining%20excellence&width=500&height=500&seq=prod-detail-007&orientation=squarish',
      name: '超小型精密ネジ',
      specs: 'M1×3mm / 医療機器用',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=hastelloy%20precision%20fastener%20on%20clean%20white%20background%2C%20macro%20photography%20showing%20corrosion-resistant%20metal%20surface%20and%20precision%20threading&width=500&height=500&seq=prod-detail-008&orientation=squarish',
      name: 'ハステロイ製ファスナー',
      specs: 'M8×25mm / 化学プラント用',
    },
    {
      image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/3dc783fd6f43e0da5549037d90fb7e95.jpeg',
      name: '特殊ピッチネジ',
      specs: 'カスタムピッチ対応',
    },
  ];

  const equipment = [
    {
      image: 'https://readdy.ai/api/search-image?query=modern%20CNC%20lathe%20machine%20in%20Japanese%20factory%2C%20high-precision%20metalworking%20equipment%2C%20clean%20industrial%20environment%2C%20professional%20manufacturing%20machinery%2C%20detailed%20view%20of%20control%20panel%20and%20cutting%20tools&width=800&height=600&seq=equipment-001&orientation=landscape',
      name: 'CNC精密旋盤',
      specs: '最大加工径: φ200mm / 最小加工径: φ1mm',
      features: '0.001mm単位の高精度加工が可能。複雑な形状のネジも自動加工できます。',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=precision%20milling%20machine%20in%20Japanese%20manufacturing%20facility%2C%20advanced%20metalworking%20equipment%2C%20clean%20workshop%20environment%2C%20detailed%20view%20of%20cutting%20head%20and%20workpiece&width=800&height=600&seq=equipment-002&orientation=landscape',
      name: '精密フライス盤',
      specs: 'テーブルサイズ: 1000×500mm',
      features: '特殊形状の加工や、複雑な溝加工に対応します。',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=precision%20measuring%20instruments%20and%20quality%20control%20equipment%20in%20Japanese%20factory%2C%20digital%20calipers%20and%20micrometers%2C%20clean%20inspection%20area%2C%20professional%20quality%20assurance%20tools&width=800&height=600&seq=equipment-003&orientation=landscape',
      name: '三次元測定機',
      specs: '測定精度: 0.001mm',
      features: '製品の寸法を高精度に測定し、品質を保証します。',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=thread%20grinding%20machine%20in%20Japanese%20precision%20factory%2C%20specialized%20metalworking%20equipment%20for%20thread%20finishing%2C%20clean%20industrial%20environment%2C%20detailed%20view%20of%20grinding%20wheel%20and%20workpiece&width=800&height=600&seq=equipment-004&orientation=landscape',
      name: 'ネジ研削盤',
      specs: '研削精度: 0.005mm',
      features: '高精度なネジ山の仕上げ加工を行います。',
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <PageHero
          title="製品・工場案内"
          breadcrumbs={[
            { label: 'トップ', path: '/' },
            { label: '製品・工場案内' },
          ]}
          imageUrl="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/ca283389bdc299f0218ee9a7a36d38d3.webp"
        />

        {/* 製品ギャラリー */}
        <section className="py-16 sm:py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy text-center mb-6 sm:mb-8">
              加工事例ギャラリー
            </h2>
            <p className="text-base sm:text-lg text-gray-600 text-center mb-12 sm:mb-16 max-w-3xl mx-auto">
              岡山市の町工場として、様々な特注ネジ・難削材加工に対応してきました
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-product-shop>
              {products.map((product, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group cursor-pointer"
                >
                  <div className="aspect-square bg-gray-50 flex items-center justify-center p-6 sm:p-8 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4 sm:p-6 text-center">
                    <h3 className="text-base sm:text-lg font-bold text-navy mb-2">
                      {product.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600">{product.specs}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 工場設備紹介 */}
        <section className="py-16 sm:py-20 lg:py-28 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy text-center mb-12 sm:mb-16 lg:mb-20">
              保有設備
            </h2>

            <div className="space-y-12 sm:space-y-16 lg:space-y-20">
              {equipment.map((item, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="rounded-2xl overflow-hidden shadow-xl">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-navy mb-4 sm:mb-6">
                      {item.name}
                    </h3>
                    <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6">
                      <strong className="text-navy">仕様:</strong> {item.specs}
                    </p>
                    <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                      {item.features}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ProductsPage;